import { useEffect, useState } from "react"
import AdHocCommands, {
  type PromptSuggestions,
  EMPTY_PROMPT_SUGGESTIONS as EMPTY,
} from "@/api/adHocCommands"

type Status = "loading" | "ready"
type State = PromptSuggestions & { status: Status }

// Module-level cache keyed by assistant JID so re-opening the chat doesn't
// refetch. Cleared only on full reload (suggestions change rarely).
const cache = new Map<string, PromptSuggestions>()
const inflight = new Map<string, Promise<PromptSuggestions>>()

/** Test-only: reset the cache between cases. */
export function __clearPromptSuggestionsCache() {
  cache.clear()
  inflight.clear()
}

export function useAgentPromptSuggestions(assistantJid: string | null): State {
  const [state, setState] = useState<State>(() =>
    assistantJid && cache.has(assistantJid)
      ? { ...(cache.get(assistantJid) ?? EMPTY), status: "ready" }
      : { ...EMPTY, status: "loading" },
  )

  useEffect(() => {
    if (!assistantJid) {
      setState({ ...EMPTY, status: "ready" })
      return
    }
    if (cache.has(assistantJid)) {
      setState({ ...(cache.get(assistantJid) ?? EMPTY), status: "ready" })
      return
    }
    let cancelled = false
    const client = window.XMPP
    if (!client) {
      console.warn(
        "[useAgentPromptSuggestions] window.XMPP not available; skipping prompt suggestions fetch",
      )
      setState({ ...EMPTY, status: "ready" })
      return
    }
    const promise =
      inflight.get(assistantJid) ??
      AdHocCommands.getPromptSuggestions(client, assistantJid).then((res) => {
        cache.set(assistantJid, res)
        inflight.delete(assistantJid)
        return res
      })
    inflight.set(assistantJid, promise)
    setState({ ...EMPTY, status: "loading" })
    void promise.then((res) => {
      if (!cancelled) setState({ ...res, status: "ready" })
    })
    return () => {
      cancelled = true
    }
  }, [assistantJid])

  return state
}
